import { Card, Button, Badge, IconButton } from "components/common";
import { FaEdit, FaExternalLinkAlt, FaHistory, FaMixer, FaPlus, FaRProject } from "react-icons/fa";
import { formatDate, formatNumber } from "helpers/utils";
import GoogleMapsLink from "helpers/googleMapsLink";
import { MdOutlineTransferWithinAStation, MdTransferWithinAStation } from "react-icons/md";
import React, { useState, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import PropTypes from 'prop-types';

const LazySalesAction = lazy(() => import("../confirmation/LeadSalesAction"));
const LazyUpdateProject = lazy(() => import("../UpdateProject"));
const LazyConvertPCVConfirm = lazy(() => import("../confirmation/ConvertPCVConfirm"));

const infoRow = (label, value) => (
  <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2">
    <div className="tw-col-span-12 sm:tw-col-span-3">{label}</div>
    <div className="tw-col-span-12 sm:tw-col-span-9">
      {value}
    </div>
  </div>
);

const Contacted = ({lead}) => {
  const [showSalesAction, setShowSalesAction] = useState(false)
  const [showUpdateProject, setShowUpdateProject] = useState(false)
  const [showConvertPCVConfirm, setShowConvertPCVConfirm] = useState(false)

  const handleAddAction = () => {
    setShowSalesAction(true);
  }

  const handleUpdateProject = () => {
    setShowUpdateProject(true);
  }

  const handleConvertPCV = () => {
    setShowConvertPCVConfirm(true);
  }

  const permissions = lead?.permissions || {};
  const contacts = lead?.contacts || [];
  const opportunity = lead?.opportunity || {};
  const customer = lead?.customer || {};

  const {
    allow_convert,
    allow_add_sales_action,
    allow_update_project
  } = permissions || {};

  return (
    <>
      <div className="tw-p-3">
        <div className="tw-text-xl tw-font-semibold">Contacted stage</div>
        <p>Contact and explore more information from customer.</p>

        <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-text-sm tw-mt-4">
          <div className="tw-col-span-12 sm:tw-col-span-8">
            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-bold tw-text-sm"><MdTransferWithinAStation className="tw-text-yellow-500" /> Transfer information</h3>}
            >
              <div className="tw-mb-4 tw-text-blue-600">The lead has been transferred to Sales for handling qualified information until it becomes a new opportunity.</div>
              {infoRow('Sales person:', <span className="tw-text-blue-600">{lead.salesperson}</span>)}
              {infoRow('Confirmed:', <span className="tw-text-blue-600">{lead.sales_confirm}</span>)}
              {infoRow('Confirmed date:', <span className="tw-text-blue-600">{lead.sales_confirm_date}</span>)}
              <div className="tw-mt-4">
                {infoRow('Sales comment:', <span className="tw-text-blue-600">{lead.sales_confirm_reason}</span>)}
              </div>
            </Card>

            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-bold tw-text-sm"><FaRProject className="tw-text-yellow-500" /> Project information</h3>}
              footer={(
                <div className="tw-text-right">
                  {allow_update_project && (
                    <IconButton
                      icon={<FaEdit size={12} />}
                      size="sm"
                      onClick={handleUpdateProject}
                    >
                      Update
                    </IconButton>
                  )}

                  {allow_convert && (
                    <Button
                      size="sm"
                      variant="warning"
                      onClick={handleConvertPCV}
                      className="tw-ml-2"
                    >
                      <MdOutlineTransferWithinAStation /> Convert PCV now!
                    </Button>
                  )}
                </div>
              )}
            >
              <div className="tw-mb-4">
                {infoRow('Project name:', <span className="tw-text-blue-600 tw-font-bold">{lead.project_name}</span>)}
              </div>
              {infoRow('Amount:', <span className="tw-text-blue-600">${formatNumber(lead.amount)}</span>)}
              {infoRow('Area:', <span className="tw-text-blue-600">{formatNumber(lead.area)} m2</span>)}

              <div className="tw-mt-4">
                {infoRow('Building type L1:', <span className="tw-text-blue-600">{lead?.building_type_l1?.name}</span>)}
              </div>
              {infoRow('Building type L2:', <span className="tw-text-blue-600">{lead?.building_type_l2?.name}</span>)}

              <div className="tw-mt-4">
                {infoRow('Location:', <GoogleMapsLink address={lead.project_location} />)}
              </div>
            </Card>
          </div>
          <div className="tw-col-span-12 sm:tw-col-span-4">
            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-bold tw-text-sm"><FaMixer className="tw-text-cyan-600" /> Sales action</h3>}
              footer={(
                <div className="tw-text-right">
                  {allow_add_sales_action && (
                    <IconButton
                      icon={<FaPlus size={12} />}
                      size="sm"
                      onClick={handleAddAction}
                    >
                      Add Action
                    </IconButton>
                  )}
                </div>
              )}
            >
              <span className="tw-text-blue-600">{lead.sales_action ? lead.sales_action : "No action found."}</span>
            </Card>

            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-bold tw-text-sm"><FaHistory className="tw-text-cyan-600" /> Tranfer history</h3>}
            >
              {infoRow('By:', <span className="tw-text-blue-600">{lead.transfer_by}</span>)}
              {infoRow('Date:', <span className="tw-text-blue-600">{formatDate(lead.transfer_date)}</span>)}
            </Card>

            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-bold tw-text-sm"><FaExternalLinkAlt className="tw-text-cyan-600" /> Related</h3>}
            >
              <div className="tw-mb-2">
                Opportunity ({opportunity?.id ? 1 : 0}): <Link to={`/crm/projects/${opportunity?.id}`} target="_blank">{opportunity?.name}</Link>
              </div>
              <div className="tw-mb-2">
                Customer ({customer?.id ? 1 : 0}): <Link to={`/crm/customers/${customer?.id}`} target="_blank">{customer?.name}</Link>
              </div>
              <div className="tw-mb-2">
                Contacts ({contacts?.length || 0}): {contacts && contacts.map(c => (
                  <Badge key={c.id} variant="default" size="sm" className="tw-mr-2">{c?.name}</Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Suspense fallback={<div>Loading...!</div>}>
        {showSalesAction && (
          <LazySalesAction
            showModal={showSalesAction}
            setshowModal={setShowSalesAction}
            lead={lead}
          />
        )}

        {showUpdateProject && (
          <LazyUpdateProject
            showModal={showUpdateProject}
            setshowModal={setShowUpdateProject}
            lead={lead}
          />
        )}

        {showConvertPCVConfirm && (
          <LazyConvertPCVConfirm
            showModal={showConvertPCVConfirm}
            setshowModal={setShowConvertPCVConfirm}
            lead={lead}
          />
        )}
      </Suspense>
    </>
  )
};

Contacted.propTypes = {
  lead: PropTypes.object.isRequired
}

export default Contacted;
